/**
 * ActiveStatusIndex — client-wide view of WHICH statuses ride WHICH targets.
 *
 * The interaction layer's second milestone (plan: fx-grammar-buildout).
 * StatusEffectManager only knows statuses THIS client applied, so reactions
 * fired only for the player who applied the status — observers saw the hit
 * but never the shatter. This index also ingests the server's wire truth
 * (WIRE_STATUS_APPLIED / WIRE_STATUS_REMOVED — the same events StatusVFX-
 * Handler draws auras from), so every client can answer "is this target
 * frozen?" regardless of who froze it.
 *
 * Deliberately tiny and read-only from the outside: targetId → statusId →
 * expiresAt, with expiry filtering at query time (no timers to leak — the
 * same design getEffects() uses). Also the future substrate for target-side
 * buff bars (the BuffBar only shows SELF statuses today).
 *
 * Singleton, CooldownStore pattern; self-subscribes on first access.
 */
import { EventBus, EVENTS } from '../../../shared/EventBus';

export interface ActiveStatusEntry {
  expiresAt: number;
  durationMs: number;
  sourceId?: string;
  data?: Record<string, unknown> | null;
  stacks?: number;
  maxStacks?: number;
}

export class ActiveStatusIndex {
  private static instance: ActiveStatusIndex | null = null;
  private byTarget = new Map<string, Map<string, ActiveStatusEntry>>();
  private listeners = new Set<(targetId: string) => void>();
  private unsubscribers: Array<() => void> = [];

  static getInstance(): ActiveStatusIndex {
    if (!this.instance) {
      this.instance = new ActiveStatusIndex();
      this.instance.installSubscriptions();
    }
    return this.instance;
  }

  /** Test seam — drops subscriptions state (fresh instance re-subscribes). */
  static resetInstance(): void {
    this.instance?.dispose();
    this.instance = null;
  }

  private installSubscriptions(): void {
    const bus = EventBus.getInstance();
    this.unsubscribers.push(bus.on(EVENTS.WIRE_STATUS_APPLIED, (d: {
      targetId?: string;
      effectId?: string;
      durationMs?: number;
      sourceSid?: string;
      data?: Record<string, unknown> | null;
    }) => {
      if (d?.targetId && d.effectId) {
        this.apply(d.targetId, d.effectId, d.durationMs ?? 10000, {
          sourceId: d.sourceSid,
          data: d.data,
          stacks: 1,
          maxStacks: 1,
        });
      }
    }));
    this.unsubscribers.push(bus.on(EVENTS.WIRE_STATUS_REMOVED, (d: { targetId?: string; effectId?: string }) => {
      if (d?.targetId && d.effectId) this.remove(d.targetId, d.effectId);
    }));
    // Local prediction lane — the caster's own applications arrive here
    // before (or without) the wire echo. Same event StatusVFXHandler draws.
    this.unsubscribers.push(bus.on('vfx:status_effect', (d: {
      targetId?: string;
      effectId?: string;
      active?: boolean;
      duration?: number;
      sourceId?: string;
      data?: Record<string, unknown> | null;
    }) => {
      if (!d?.targetId || !d.effectId) return;
      if (d.active) {
        this.apply(d.targetId, d.effectId, d.duration ?? 10000, {
          sourceId: d.sourceId,
          data: d.data,
          stacks: 1,
          maxStacks: 1,
        });
      }
      else this.remove(d.targetId, d.effectId);
    }));
    this.unsubscribers.push(bus.on('vfx:status_stack_update', (d: {
      targetId?: string;
      effectId?: string;
      duration?: number;
      sourceId?: string;
      data?: Record<string, unknown> | null;
      stacks?: number;
      maxStacks?: number;
    }) => {
      if (!d?.targetId || !d.effectId) return;
      this.apply(d.targetId, d.effectId, d.duration ?? 10000, {
        sourceId: d.sourceId,
        data: d.data,
        stacks: d.stacks,
        maxStacks: d.maxStacks,
      });
    }));
  }

  apply(
    targetId: string,
    statusId: string,
    durationMs: number,
    metadata: Omit<ActiveStatusEntry, 'expiresAt' | 'durationMs'> = {},
  ): void {
    if (!this.byTarget.has(targetId)) this.byTarget.set(targetId, new Map());
    const existing = this.byTarget.get(targetId)!.get(statusId);
    this.byTarget.get(targetId)!.set(statusId, {
      ...existing,
      ...metadata,
      expiresAt: Date.now() + durationMs,
      durationMs,
    });
    this.notify(targetId);
  }

  remove(targetId: string, statusId: string): void {
    const removed = this.byTarget.get(targetId)?.delete(statusId) ?? false;
    if (removed) this.notify(targetId);
  }

  subscribe(listener: (targetId: string) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Live status ids on a target (expired entries filtered + pruned). */
  getStatuses(targetId: string): string[] {
    return this.getStatusEntries(targetId).map(([id]) => id);
  }

  /** Live [statusId, {expiresAt, durationMs}] pairs — the duration-aware read
   *  target buff pips fade on (buff-bars spec integration #1/#2). Same
   *  query-time expiry + prune as getStatuses. */
  getStatusEntries(targetId: string): Array<[string, ActiveStatusEntry]> {
    const m = this.byTarget.get(targetId);
    if (!m) return [];
    const now = Date.now();
    const out: Array<[string, ActiveStatusEntry]> = [];
    for (const [id, entry] of m) {
      if (entry.expiresAt > now) out.push([id, entry]);
      else m.delete(id);
    }
    return out;
  }

  private notify(targetId: string): void {
    for (const listener of this.listeners) listener(targetId);
  }

  private dispose(): void {
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers = [];
    this.listeners.clear();
    this.byTarget.clear();
  }
}
